import React, { useEffect, useState, useMemo } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";
import { Briefcase, FileText, Plus, PencilSimple, Trash, Check, X as XIcon } from "@phosphor-icons/react";
import AppointmentFilesDialog from "../../components/AppointmentFilesDialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../../components/ui/dialog";

const STATUS_COLORS = { active: "#34d399", pending: "#2563eb", terminated: "#fb7185" };
const STATUSES = ["active", "pending", "terminated"];

const BLANK = (defaultAgentId) => ({
  agent_id: defaultAgentId || "",
  carrier_id: "",
  product_type: "",
  writing_code: "",
  status: "active",
  effective_date: "",
  notes: "",
});

export default function MyAppointments() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isPrivileged = ["managing_partner", "field_director"].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [downline, setDownline] = useState([]); // for MP / FD agent picker
  const [filesFor, setFilesFor] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK(user?.id));
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [a, c, ...rest] = await Promise.all([
      api.get("/appointments"),
      api.get("/carriers"),
      isPrivileged ? api.get("/users") : Promise.resolve({ data: [] }),
    ]);
    setRows(a.data || []);
    setCarriers(c.data || []);
    if (isPrivileged) {
      // Only show the caller + their agents in the picker
      const pool = (rest[0]?.data || []).filter(
        (u) => u.id === user.id || u.managing_partner_id === user.id || u.field_director_id === user.id
      );
      setDownline(pool);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openNew = () => {
    setEditing(null);
    setForm({ ...BLANK(user.id) });
    setOpen(true);
  };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      agent_id: r.agent_id,
      carrier_id: r.carrier_id,
      product_type: r.product_type || "",
      writing_code: r.writing_code || "",
      status: r.status || "active",
      effective_date: r.effective_date || "",
      notes: r.notes || "",
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        // Server forces non-admins' comp_level — still send something valid for the schema.
        comp_level: editing ? Number(editing.comp_level || 0) : 0,
        effective_date: form.effective_date || null,
        notes: form.notes || null,
        writing_code: form.writing_code || null,
      };
      if (editing) await api.patch(`/appointments/${editing.id}`, payload);
      else await api.post("/appointments", payload);
      toast.success(editing ? "Appointment updated" : "Appointment added");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const del = async (r) => {
    if (!window.confirm(`Remove your appointment with ${r.carrier_name}?`)) return;
    try {
      await api.delete(`/appointments/${r.id}`);
      toast.success("Removed");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not remove");
    }
  };

  const canEditRow = (r) =>
    isAdmin || r.agent_id === user.id || (isPrivileged && downline.some((d) => d.id === r.agent_id));

  const agentName = useMemo(() => {
    const m = {};
    downline.forEach((d) => { m[d.id] = d.name; });
    rows.forEach((r) => { if (r.agent_id && r.agent_name) m[r.agent_id] = r.agent_name; });
    return m;
  }, [downline, rows]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-zinc-500">My Contracts</div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mt-1">Carrier Appointments</h1>
          <p className="text-zinc-600 mt-2">
            Add your carriers + writing code. Compensation levels are managed by your admin.
          </p>
        </div>
        <Button
          onClick={openNew}
          className="rounded-none bg-[#09090b] text-white border-2 border-black hover:bg-black shadow-[4px_4px_0_0_#2563eb] font-bold uppercase tracking-widest text-xs h-11"
          data-testid="add-my-appt-btn"
        >
          <Plus size={16} weight="bold" className="mr-1" /> Add Carrier
        </Button>
      </div>

      <div className="brut bg-white overflow-x-auto">
        <table className="w-full border-collapse" data-testid="my-appointments-table">
          <thead>
            <tr className="bg-[#09090b] text-white">
              {isPrivileged && <th className="text-left px-4 py-3 font-bold uppercase text-xs tracking-widest">Agent</th>}
              <th className="text-left px-4 py-3 font-bold uppercase text-xs tracking-widest">Carrier</th>
              <th className="text-left px-4 py-3 font-bold uppercase text-xs tracking-widest">Product</th>
              <th className="text-left px-4 py-3 font-bold uppercase text-xs tracking-widest">Writing Code</th>
              <th className="text-left px-4 py-3 font-bold uppercase text-xs tracking-widest">Status</th>
              <th className="text-right px-4 py-3 font-bold uppercase text-xs tracking-widest">Comp Level</th>
              <th className="text-left px-4 py-3 font-bold uppercase text-xs tracking-widest">Effective</th>
              <th className="text-right px-4 py-3 font-bold uppercase text-xs tracking-widest">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b-2 border-black last:border-b-0 hover:bg-[#f4f4f5]" data-testid={`my-appt-row-${r.id}`}>
                {isPrivileged && <td className="px-4 py-3 font-medium text-zinc-700">{agentName[r.agent_id] || r.agent_name || "—"}</td>}
                <td className="px-4 py-3">
                  <span className="inline-block border-2 border-black px-2.5 py-1 font-bold text-sm" style={{ background: r.carrier_color || "#2563eb", color: "#09090b" }}>
                    {r.carrier_name}
                  </span>
                </td>
                <td className="px-4 py-3 font-medium">{r.product_type}</td>
                <td className="px-4 py-3 font-mono text-sm">{r.writing_code || <span className="text-zinc-400">—</span>}</td>
                <td className="px-4 py-3">
                  <span className="inline-block border-2 border-black px-2 py-0.5 text-xs font-bold uppercase tracking-widest" style={{ background: STATUS_COLORS[r.status] || "#e4e4e7" }}>
                    {r.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-right font-display font-black text-xl tracking-tight">{r.comp_level ? `${r.comp_level}%` : <span className="text-zinc-400">—</span>}</td>
                <td className="px-4 py-3 text-zinc-600">{r.effective_date || "—"}</td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-2">
                    <button onClick={() => setFilesFor(r)} className="border-2 border-black p-2 hover:bg-[#34d399]" data-testid={`my-files-${r.id}`} title="View files">
                      <FileText size={14} weight="bold" />
                    </button>
                    {canEditRow(r) && (
                      <>
                        <button onClick={() => openEdit(r)} className="border-2 border-black p-2 hover:bg-[#2563eb] hover:text-white" data-testid={`edit-my-appt-${r.id}`} title="Edit">
                          <PencilSimple size={14} weight="bold" />
                        </button>
                        <button onClick={() => del(r)} className="border-2 border-black p-2 hover:bg-[#fb7185] hover:text-white" data-testid={`delete-my-appt-${r.id}`} title="Remove">
                          <Trash size={14} weight="bold" />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={isPrivileged ? 8 : 7} className="px-4 py-10 text-center text-zinc-500">
                <Briefcase size={28} className="mx-auto mb-2" /> No carrier appointments yet. Click &ldquo;Add Carrier&rdquo; to start.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="border-2 border-black rounded-none shadow-[6px_6px_0_0_#09090b] max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-display font-black tracking-tight">
              {editing ? "Edit Carrier Appointment" : "Add Carrier Appointment"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              {isPrivileged && (
                <div className="col-span-2">
                  <Label className="font-bold uppercase text-xs tracking-widest">Agent</Label>
                  <select
                    value={form.agent_id}
                    onChange={(e) => setForm({ ...form, agent_id: e.target.value })}
                    required
                    className="mt-1 w-full h-11 border-2 border-black px-3 bg-white font-medium"
                    data-testid="my-appt-agent"
                  >
                    {downline.map((d) => <option key={d.id} value={d.id}>{d.name}{d.id === user.id ? " (You)" : ""}</option>)}
                  </select>
                </div>
              )}
              <div>
                <Label className="font-bold uppercase text-xs tracking-widest">Carrier</Label>
                <select
                  value={form.carrier_id}
                  onChange={(e) => setForm({ ...form, carrier_id: e.target.value })}
                  required
                  className="mt-1 w-full h-11 border-2 border-black px-3 bg-white font-medium"
                  data-testid="my-appt-carrier"
                >
                  <option value="">Select…</option>
                  {carriers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <Label className="font-bold uppercase text-xs tracking-widest">Product</Label>
                <Input
                  value={form.product_type}
                  onChange={(e) => setForm({ ...form, product_type: e.target.value })}
                  required
                  placeholder="IUL / Annuity / Term"
                  className="mt-1 border-2 border-black rounded-none h-11 focus-visible:ring-0"
                  data-testid="my-appt-product"
                />
              </div>
              <div>
                <Label className="font-bold uppercase text-xs tracking-widest">Writing Code</Label>
                <Input
                  value={form.writing_code}
                  onChange={(e) => setForm({ ...form, writing_code: e.target.value })}
                  placeholder="e.g. WC-12345"
                  className="mt-1 border-2 border-black rounded-none h-11 focus-visible:ring-0 font-mono"
                  data-testid="my-appt-writing-code"
                />
              </div>
              <div>
                <Label className="font-bold uppercase text-xs tracking-widest">Status</Label>
                <select
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                  className="mt-1 w-full h-11 border-2 border-black px-3 bg-white font-medium"
                  data-testid="my-appt-status"
                >
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <Label className="font-bold uppercase text-xs tracking-widest">Effective Date</Label>
                <Input
                  type="date"
                  value={form.effective_date}
                  onChange={(e) => setForm({ ...form, effective_date: e.target.value })}
                  className="mt-1 border-2 border-black rounded-none h-11 focus-visible:ring-0"
                  data-testid="my-appt-effective"
                />
              </div>
              <div className="col-span-2">
                <Label className="font-bold uppercase text-xs tracking-widest">Notes</Label>
                <Input
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="mt-1 border-2 border-black rounded-none h-11 focus-visible:ring-0"
                />
              </div>
            </div>
            {!isAdmin && (
              <div className="border-2 border-dashed border-zinc-300 p-3 text-xs text-zinc-500">
                Compensation levels are set by your administrator and cannot be edited here.
              </div>
            )}
            <div className="flex gap-2 justify-end">
              <Button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-none bg-white text-black border-2 border-black hover:bg-zinc-100 font-bold uppercase text-xs tracking-widest h-11"
                data-testid="cancel-my-appt-btn"
              >
                <XIcon size={14} weight="bold" className="mr-1" /> Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="rounded-none bg-[#2563eb] text-white border-2 border-black shadow-[4px_4px_0_0_#09090b] hover:shadow-[6px_6px_0_0_#09090b] hover:-translate-y-0.5 font-bold uppercase tracking-widest h-11"
                data-testid="save-my-appt-btn"
              >
                <Check size={14} weight="bold" className="mr-1" /> {saving ? "Saving…" : "Save"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <AppointmentFilesDialog
        appointment={filesFor}
        open={!!filesFor}
        onClose={() => setFilesFor(null)}
        canEdit={!!filesFor && (filesFor.agent_id === user.id || isAdmin || (isPrivileged && downline.some((d) => d.id === filesFor.agent_id)))}
      />
    </div>
  );
}
